import Vue from 'vue';
import { $handleError } from '@/utils';
import { warn } from '@/utils/debugLogger';
import jiraCatalogService from '@ReleaseNotes/services/jiraCatalog.service';
import releasesService from '@ReleaseNotes/services/releases.service';
import releaseNotesSettingsService from '@ReleaseNotes/services/releaseNotesSettings.service';
import tenantReleasesService from '@ReleaseNotes/services/tenantReleases.service';
import { isFeatureDisabledError, releaseErrorCode } from '@ReleaseNotes/config/enums';

/**
 * True for the optimistic-concurrency conflict returned by PUT {id}/drafts and POST generate.
 * @param {*} err rejected axios error
 */
function isConflictError(err) {
  return !!(err && err.response && err.response.status === 409);
}

/**
 * True for the 400 returned by POST {id}/publish when no eligible recipients resolve.
 * @param {*} err rejected axios error
 */
function isAudienceEmptyError(err) {
  return !!(
    err &&
    err.response &&
    err.response.status === 400 &&
    releaseErrorCode(err) === 'RELEASE_AUDIENCE_EMPTY'
  );
}

/**
 * Normalises a list response body whose shape is unverified. The API may return either a bare
 * array or a paged envelope; both collapse to `{ items, total }` so no getter ever sees
 * `undefined` and no commit throws on a missing property.
 * @param {*} data response body
 * @returns {{ items: Array, total: Number }}
 */
function unwrapList(data) {
  if (Array.isArray(data)) return { items: data, total: data.length };
  // Some of these routes wrap their page in a `data` envelope (plan addendum §A.5 flags every
  // paged shape here as unverified); unwrap one level before giving up and returning nothing.
  const body = data?.data ?? data;
  if (Array.isArray(body)) return { items: body, total: data?.total ?? body.length };
  return { items: body?.items ?? [], total: body?.total ?? data?.total ?? 0 };
}

export const RELEASE_NOTES = {
  namespaced: true,
  state: {
    // list
    releases: [],
    releasesTotal: 0,
    releasesLoading: false,
    releaseListOptions: { itemsPerPage: 15, sortBy: [], sortDesc: [] },
    releaseListStatus: null,
    releaseListSearch: '',
    releaseNotesDisabled: false,

    // jira catalogue
    jiraProjects: [],
    jiraProjectsLoading: false,
    jiraVersions: [],
    jiraVersionsTotal: 0,
    jiraVersionsLoading: false,
    jiraIssues: [],
    jiraIssuesLoading: false,

    // detail (single release workspace)
    currentRelease: null,
    currentReleaseId: null,
    currentReleaseLoading: false,
    drafts: [],
    draftsLoading: false,
    draftSaving: false,
    regenerating: false,
    generationPolling: false,
    itemsSaving: false,

    // publish
    companyOptions: [],
    companySearchLoading: false,
    publishPreview: null,
    publishPreviewLoading: false,
    publishing: false,
    publications: [],
    publicationsLoading: false,
    deliveries: [],
    deliveriesTotal: 0,
    deliveriesLoading: false,

    // reminders
    reminders: [],
    remindersLoading: false,

    // settings
    settings: null,
    settingsLoading: false,
    settingsSaving: false,

    // tenant surface
    tenantReleases: [],
    tenantRelease: null,
    tenantLoading: false,
    // Slug-keyed body cache for the What's New changelog, which renders many release bodies on
    // one scroll. Deliberately separate from `tenantRelease` / `tenantLoading`: those belong to
    // the single-release detail page, and writing them here would clobber it and flash the
    // changelog's list skeleton on every lazy body fetch.
    tenantReleaseBodies: {},
    tenantBodyLoadingSlugs: [],
    subscription: null,
  },
  getters: {
    GET_RELEASES(state) {
      return state.releases;
    },
    GET_RELEASES_TOTAL(state) {
      return state.releasesTotal;
    },
    IS_RELEASES_LOADING(state) {
      return state.releasesLoading;
    },
    GET_RELEASE_LIST_OPTIONS(state) {
      return state.releaseListOptions;
    },
    GET_RELEASE_LIST_STATUS(state) {
      return state.releaseListStatus;
    },
    GET_RELEASE_LIST_SEARCH(state) {
      return state.releaseListSearch;
    },
    IS_RELEASE_NOTES_DISABLED(state) {
      return state.releaseNotesDisabled;
    },
    GET_JIRA_PROJECTS(state) {
      return state.jiraProjects;
    },
    IS_JIRA_PROJECTS_LOADING(state) {
      return state.jiraProjectsLoading;
    },
    GET_JIRA_VERSIONS(state) {
      return state.jiraVersions;
    },
    GET_JIRA_VERSIONS_TOTAL(state) {
      return state.jiraVersionsTotal;
    },
    IS_JIRA_VERSIONS_LOADING(state) {
      return state.jiraVersionsLoading;
    },
    GET_JIRA_ISSUES(state) {
      return state.jiraIssues;
    },
    IS_JIRA_ISSUES_LOADING(state) {
      return state.jiraIssuesLoading;
    },
    GET_CURRENT_RELEASE(state) {
      return state.currentRelease;
    },
    GET_CURRENT_RELEASE_ID(state) {
      return state.currentReleaseId;
    },
    IS_CURRENT_RELEASE_LOADING(state) {
      return state.currentReleaseLoading;
    },
    GET_DRAFTS(state) {
      return state.drafts;
    },
    IS_DRAFTS_LOADING(state) {
      return state.draftsLoading;
    },
    IS_DRAFT_SAVING(state) {
      return state.draftSaving;
    },
    IS_REGENERATING(state) {
      return state.regenerating;
    },
    IS_GENERATION_POLLING(state) {
      return state.generationPolling;
    },
    IS_ITEMS_SAVING(state) {
      return state.itemsSaving;
    },
    GET_COMPANY_OPTIONS(state) {
      return state.companyOptions;
    },
    IS_COMPANY_SEARCH_LOADING(state) {
      return state.companySearchLoading;
    },
    GET_PUBLISH_PREVIEW(state) {
      return state.publishPreview;
    },
    IS_PUBLISH_PREVIEW_LOADING(state) {
      return state.publishPreviewLoading;
    },
    IS_PUBLISHING(state) {
      return state.publishing;
    },
    GET_PUBLICATIONS(state) {
      return state.publications;
    },
    IS_PUBLICATIONS_LOADING(state) {
      return state.publicationsLoading;
    },
    GET_DELIVERIES(state) {
      return state.deliveries;
    },
    GET_DELIVERIES_TOTAL(state) {
      return state.deliveriesTotal;
    },
    IS_DELIVERIES_LOADING(state) {
      return state.deliveriesLoading;
    },
    GET_REMINDERS(state) {
      return state.reminders;
    },
    IS_REMINDERS_LOADING(state) {
      return state.remindersLoading;
    },
    GET_SETTINGS(state) {
      return state.settings;
    },
    IS_SETTINGS_LOADING(state) {
      return state.settingsLoading;
    },
    IS_SETTINGS_SAVING(state) {
      return state.settingsSaving;
    },
    GET_TENANT_RELEASES(state) {
      return state.tenantReleases;
    },
    GET_TENANT_RELEASE(state) {
      return state.tenantRelease;
    },
    IS_TENANT_LOADING(state) {
      return state.tenantLoading;
    },
    GET_TENANT_RELEASE_BODIES(state) {
      return state.tenantReleaseBodies;
    },
    GET_TENANT_BODY_LOADING_SLUGS(state) {
      return state.tenantBodyLoadingSlugs;
    },
    GET_SUBSCRIPTION(state) {
      return state.subscription;
    },
  },
  mutations: {
    SET_RELEASES(state, value) {
      state.releases = value;
    },
    SET_RELEASES_TOTAL(state, value) {
      state.releasesTotal = value;
    },
    SET_RELEASES_LOADING(state, value) {
      state.releasesLoading = value;
    },
    SET_RELEASE_LIST_OPTIONS(state, value) {
      state.releaseListOptions = value;
    },
    SET_RELEASE_LIST_STATUS(state, value) {
      state.releaseListStatus = value;
    },
    SET_RELEASE_LIST_SEARCH(state, value) {
      state.releaseListSearch = value;
    },
    SET_RELEASE_NOTES_DISABLED(state, value) {
      state.releaseNotesDisabled = value;
    },
    SET_JIRA_PROJECTS(state, value) {
      state.jiraProjects = value;
    },
    SET_JIRA_PROJECTS_LOADING(state, value) {
      state.jiraProjectsLoading = value;
    },
    SET_JIRA_VERSIONS(state, value) {
      state.jiraVersions = value;
    },
    SET_JIRA_VERSIONS_TOTAL(state, value) {
      state.jiraVersionsTotal = value;
    },
    SET_JIRA_VERSIONS_LOADING(state, value) {
      state.jiraVersionsLoading = value;
    },
    SET_JIRA_ISSUES(state, value) {
      state.jiraIssues = value;
    },
    SET_JIRA_ISSUES_LOADING(state, value) {
      state.jiraIssuesLoading = value;
    },
    SET_CURRENT_RELEASE(state, value) {
      state.currentRelease = value;
    },
    SET_CURRENT_RELEASE_ID(state, value) {
      state.currentReleaseId = value;
    },
    SET_CURRENT_RELEASE_LOADING(state, value) {
      state.currentReleaseLoading = value;
    },
    SET_DRAFTS(state, value) {
      state.drafts = value;
    },
    SET_DRAFTS_LOADING(state, value) {
      state.draftsLoading = value;
    },
    SET_DRAFT_SAVING(state, value) {
      state.draftSaving = value;
    },
    SET_REGENERATING(state, value) {
      state.regenerating = value;
    },
    SET_GENERATION_POLLING(state, value) {
      state.generationPolling = value;
    },
    SET_ITEMS_SAVING(state, value) {
      state.itemsSaving = value;
    },
    SET_COMPANY_OPTIONS(state, value) {
      state.companyOptions = value;
    },
    SET_COMPANY_SEARCH_LOADING(state, value) {
      state.companySearchLoading = value;
    },
    SET_PUBLISH_PREVIEW(state, value) {
      state.publishPreview = value;
    },
    SET_PUBLISH_PREVIEW_LOADING(state, value) {
      state.publishPreviewLoading = value;
    },
    SET_PUBLISHING(state, value) {
      state.publishing = value;
    },
    SET_PUBLICATIONS(state, value) {
      state.publications = value;
    },
    SET_PUBLICATIONS_LOADING(state, value) {
      state.publicationsLoading = value;
    },
    SET_DELIVERIES(state, value) {
      state.deliveries = value;
    },
    SET_DELIVERIES_TOTAL(state, value) {
      state.deliveriesTotal = value;
    },
    SET_DELIVERIES_LOADING(state, value) {
      state.deliveriesLoading = value;
    },
    SET_REMINDERS(state, value) {
      state.reminders = value;
    },
    SET_REMINDERS_LOADING(state, value) {
      state.remindersLoading = value;
    },
    SET_SETTINGS(state, value) {
      state.settings = value;
    },
    SET_SETTINGS_LOADING(state, value) {
      state.settingsLoading = value;
    },
    SET_SETTINGS_SAVING(state, value) {
      state.settingsSaving = value;
    },
    SET_TENANT_RELEASES(state, value) {
      state.tenantReleases = value;
    },
    SET_TENANT_RELEASE(state, value) {
      state.tenantRelease = value;
    },
    SET_TENANT_LOADING(state, value) {
      state.tenantLoading = value;
    },
    // `Vue.set` because the cache starts empty and every slug is a brand-new key; a plain
    // assignment would not be reactive in Vue 2.
    SET_TENANT_RELEASE_BODY(state, { slug, body }) {
      Vue.set(state.tenantReleaseBodies, slug, body);
    },
    ADD_TENANT_BODY_LOADING(state, slug) {
      if (!state.tenantBodyLoadingSlugs.includes(slug)) {
        state.tenantBodyLoadingSlugs = state.tenantBodyLoadingSlugs.concat(slug);
      }
    },
    REMOVE_TENANT_BODY_LOADING(state, slug) {
      state.tenantBodyLoadingSlugs = state.tenantBodyLoadingSlugs.filter((it) => it !== slug);
    },
    SET_SUBSCRIPTION(state, value) {
      state.subscription = value;
    },
  },
  actions: {
    // ------------------------------------------------------------------ list
    FETCH_RELEASES({ commit, state }) {
      commit('SET_RELEASES_LOADING', true);
      return releasesService
        .get({
          ...state.releaseListOptions,
          status: state.releaseListStatus,
          search: state.releaseListSearch,
        })
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_RELEASE_NOTES_DISABLED', false);
          commit('SET_RELEASES', items);
          commit('SET_RELEASES_TOTAL', total);
          return { items, total };
        })
        .catch((err) => {
          // A 404 on the base list route means the server feature flag is off — the screen
          // renders a "not enabled yet" notice instead of an error toast.
          if (isFeatureDisabledError(err)) {
            warn('[RELEASE_NOTES] Feature is disabled server-side (404 on the release list).');
            commit('SET_RELEASE_NOTES_DISABLED', true);
            commit('SET_RELEASES', []);
            commit('SET_RELEASES_TOTAL', 0);
            return;
          }
          $handleError(err);
        })
        .finally(() => {
          commit('SET_RELEASES_LOADING', false);
        });
    },

    // ---------------------------------------------------------------- detail
    FETCH_RELEASE_DETAIL({ commit }, id) {
      commit('SET_CURRENT_RELEASE_LOADING', true);
      return releasesService
        .getById(id)
        .then((res) => {
          commit('SET_CURRENT_RELEASE', res.data);
          commit('SET_CURRENT_RELEASE_ID', id);
          return res.data;
        })
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_CURRENT_RELEASE_LOADING', false);
        });
    },

    CREATE_MANUAL_RELEASE({ commit }, data) {
      commit('SET_CURRENT_RELEASE_LOADING', true);
      return releasesService
        .createManual(data)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_CURRENT_RELEASE_LOADING', false);
        });
    },

    /**
     * POST generate. A 409 (a release already exists for this Jira version, body carries
     * `existingReleaseId`) is caller-handled — rejected WITHOUT an error toast.
     */
    GENERATE({ commit }, data) {
      commit('SET_REGENERATING', true);
      return releasesService
        .generate(data)
        .then((res) => res.data)
        .catch((err) => {
          if (!isConflictError(err)) $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_REGENERATING', false);
        });
    },

    REGENERATE_DRAFT({ commit }, { id, data }) {
      commit('SET_REGENERATING', true);
      return releasesService
        .regenerateDraft(id, data)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_REGENERATING', false);
        });
    },

    RENAME_RELEASE({ commit }, { id, data }) {
      commit('SET_CURRENT_RELEASE_LOADING', true);
      return releasesService
        .rename(id, data)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_CURRENT_RELEASE_LOADING', false);
        });
    },

    ARCHIVE_RELEASE({ commit }, id) {
      commit('SET_CURRENT_RELEASE_LOADING', true);
      return releasesService
        .archive(id)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_CURRENT_RELEASE_LOADING', false);
        });
    },

    MARK_APP_RELEASED({ commit }, id) {
      commit('SET_CURRENT_RELEASE_LOADING', true);
      return releasesService
        .markAppReleased(id)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_CURRENT_RELEASE_LOADING', false);
        });
    },

    // ----------------------------------------------------------------- items
    UPDATE_ITEMS({ commit }, { id, data }) {
      commit('SET_ITEMS_SAVING', true);
      return releasesService
        .updateItems(id, data)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_ITEMS_SAVING', false);
        });
    },

    UPDATE_ITEM({ commit }, { id, itemId, data }) {
      commit('SET_ITEMS_SAVING', true);
      return releasesService
        .updateItem(id, itemId, data)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_ITEMS_SAVING', false);
        });
    },

    // ---------------------------------------------------------------- drafts
    FETCH_DRAFTS({ commit }, payload) {
      // Accepts a bare id (the customer stream, which is the server-side default) or { id, kind }.
      const { id, kind } = typeof payload === 'object' && payload !== null ? payload : { id: payload };
      commit('SET_DRAFTS_LOADING', true);
      return releasesService
        .getDrafts(id, kind)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_DRAFTS', items);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_DRAFTS_LOADING', false);
        });
    },

    FETCH_DRAFT({ commit }, { id, draftId }) {
      commit('SET_DRAFTS_LOADING', true);
      return releasesService
        .getDraft(id, draftId)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_DRAFTS_LOADING', false);
        });
    },

    /**
     * PUT {id}/drafts. A 409 is caller-handled — DraftEditor opens its conflict dialog and reads
     * `err.response.data.currentDraft`, so the conflict must not be toasted or swallowed here.
     */
    SAVE_DRAFT({ commit }, { id, data }) {
      commit('SET_DRAFT_SAVING', true);
      return releasesService
        .saveDraft(id, data)
        .then((res) => res.data)
        .catch((err) => {
          if (!isConflictError(err)) $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_DRAFT_SAVING', false);
        });
    },

    REVERT_DRAFT({ commit }, { id, draftId }) {
      commit('SET_DRAFT_SAVING', true);
      return releasesService
        .revertDraft(id, draftId)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_DRAFT_SAVING', false);
        });
    },

    /** Rebuilds the internal notes from the current customer notes + the linked-ticket appendix. */
    REBUILD_INTERNAL_NOTES({ commit }, id) {
      commit('SET_DRAFT_SAVING', true);
      return releasesService
        .rebuildInternalNotes(id)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_DRAFT_SAVING', false);
        });
    },

    // --------------------------------------------------------------- publish
    SEARCH_COMPANIES({ commit }, params) {
      commit('SET_COMPANY_SEARCH_LOADING', true);
      return releasesService
        .getCompanies(params)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_COMPANY_OPTIONS', items);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_COMPANY_SEARCH_LOADING', false);
        });
    },

    FETCH_PUBLISH_PREVIEW({ commit }, { id, data }) {
      commit('SET_PUBLISH_PREVIEW_LOADING', true);
      return releasesService
        .publishPreview(id, data)
        .then((res) => {
          commit('SET_PUBLISH_PREVIEW', res.data);
          return res.data;
        })
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_PUBLISH_PREVIEW_LOADING', false);
        });
    },

    /**
     * POST {id}/publish. A 400 RELEASE_AUDIENCE_EMPTY is caller-handled — PublishDialog shows it
     * inline next to the audience picker instead of toasting it.
     */
    PUBLISH({ commit }, { id, data }) {
      commit('SET_PUBLISHING', true);
      return releasesService
        .publish(id, data)
        .then((res) => res.data)
        .catch((err) => {
          if (!isAudienceEmptyError(err)) $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_PUBLISHING', false);
        });
    },

    SEND_TEST({ commit }, id) {
      commit('SET_PUBLISHING', true);
      return releasesService
        .sendTest(id)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_PUBLISHING', false);
        });
    },

    FETCH_PUBLICATIONS({ commit }, id) {
      commit('SET_PUBLICATIONS_LOADING', true);
      return releasesService
        .getPublications(id)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_PUBLICATIONS', items);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_PUBLICATIONS_LOADING', false);
        });
    },

    FETCH_DELIVERIES({ commit }, { publicationId, params }) {
      commit('SET_DELIVERIES_LOADING', true);
      return releasesService
        .getDeliveries(publicationId, params)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_DELIVERIES', items);
          commit('SET_DELIVERIES_TOTAL', total);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_DELIVERIES_LOADING', false);
        });
    },

    RETRY_FAILED({ commit }, publicationId) {
      commit('SET_PUBLICATIONS_LOADING', true);
      return releasesService
        .retryFailed(publicationId)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_PUBLICATIONS_LOADING', false);
        });
    },

    // ------------------------------------------------------------- reminders
    FETCH_REMINDERS({ commit }, id) {
      commit('SET_REMINDERS_LOADING', true);
      return releasesService
        .getReminders(id)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_REMINDERS', items);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_REMINDERS_LOADING', false);
        });
    },

    UPDATE_REMINDER({ commit }, { id, reminderId, data }) {
      commit('SET_REMINDERS_LOADING', true);
      return releasesService
        .updateReminder(id, reminderId, data)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_REMINDERS_LOADING', false);
        });
    },

    CANCEL_REMINDER({ commit }, { id, reminderId }) {
      commit('SET_REMINDERS_LOADING', true);
      return releasesService
        .cancelReminder(id, reminderId)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_REMINDERS_LOADING', false);
        });
    },

    SEND_REMINDER_NOW({ commit }, { id, reminderId }) {
      commit('SET_REMINDERS_LOADING', true);
      return releasesService
        .sendReminderNow(id, reminderId)
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_REMINDERS_LOADING', false);
        });
    },

    // -------------------------------------------------------- jira catalogue
    /**
     * Re-throws: `JiraVersionPicker` gates its first render on telling a 404 (the Jira
     * integration is not enabled) apart from a transient failure.
     */
    FETCH_JIRA_PROJECTS({ commit }) {
      commit('SET_JIRA_PROJECTS_LOADING', true);
      return jiraCatalogService
        .getProjects()
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_JIRA_PROJECTS', items);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_JIRA_PROJECTS_LOADING', false);
        });
    },

    FETCH_JIRA_VERSIONS({ commit }, params) {
      commit('SET_JIRA_VERSIONS_LOADING', true);
      return jiraCatalogService
        .getVersions(params)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_JIRA_VERSIONS', items);
          commit('SET_JIRA_VERSIONS_TOTAL', total);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_JIRA_VERSIONS_LOADING', false);
        });
    },

    FETCH_JIRA_VERSION_ISSUES({ commit }, versionId) {
      commit('SET_JIRA_ISSUES_LOADING', true);
      return jiraCatalogService
        .getVersionIssues(versionId)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_JIRA_ISSUES', items);
          return { items, total };
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_JIRA_ISSUES_LOADING', false);
        });
    },

    FETCH_JIRA_HEALTH() {
      return jiraCatalogService
        .getHealth()
        .then((res) => res.data)
        .catch((err) => {
          $handleError(err);
          throw err;
        });
    },

    // -------------------------------------------------------------- settings
    /**
     * Re-throws: `ReleaseNotesSettings` gates its first render on telling a 404 (feature off)
     * apart from a transient failure, and `PublishDialog` falls back to its own defaults.
     */
    FETCH_SETTINGS({ commit }) {
      commit('SET_SETTINGS_LOADING', true);
      return releaseNotesSettingsService
        .get()
        .then((res) => {
          commit('SET_SETTINGS', res.data);
          return res.data;
        })
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_SETTINGS_LOADING', false);
        });
    },

    UPDATE_SETTINGS({ commit }, data) {
      commit('SET_SETTINGS_SAVING', true);
      return releaseNotesSettingsService
        .update(data)
        .then((res) => {
          commit('SET_SETTINGS', res.data);
          return res.data;
        })
        .catch((err) => {
          $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_SETTINGS_SAVING', false);
        });
    },

    // ---------------------------------------------------------------- tenant
    /**
     * Re-throws so `WhatsNew` can tell "nothing published / not available" from a transient
     * failure on its first load. A 404 is not toasted — the view renders its own inline empty
     * state, and two signals for one condition is noise.
     */
    FETCH_TENANT_RELEASES({ commit }, params) {
      commit('SET_TENANT_LOADING', true);
      return tenantReleasesService
        .get(params)
        .then((res) => {
          const { items, total } = unwrapList(res.data);
          commit('SET_TENANT_RELEASES', items);
          return { items, total };
        })
        .catch((err) => {
          if (!isFeatureDisabledError(err)) $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_TENANT_LOADING', false);
        });
    },

    /**
     * Re-throws so `ReleaseDetailPublic` can tell a stale slug from a transient failure. A 404 is
     * not toasted — the view renders "Release not found" inline instead.
     */
    FETCH_TENANT_RELEASE({ commit }, slug) {
      commit('SET_TENANT_LOADING', true);
      return tenantReleasesService
        .getBySlug(slug)
        .then((res) => {
          commit('SET_TENANT_RELEASE', res.data);
          return res.data;
        })
        .catch((err) => {
          // Drop the previously loaded release so the page cannot show a stale title next to
          // its "not found" body.
          commit('SET_TENANT_RELEASE', null);
          if (!isFeatureDisabledError(err)) $handleError(err);
          throw err;
        })
        .finally(() => {
          commit('SET_TENANT_LOADING', false);
        });
    },

    /**
     * Loads one release body for the What's New changelog and caches it under its slug.
     *
     * Unlike `FETCH_TENANT_RELEASE` this never toasts and never rejects: the changelog shows
     * many releases at once, so a single unreachable body must degrade to an inline notice on
     * that one entry rather than a page-level error. Resolves to the cache record.
     *
     * @param {string} slug release slug
     * @returns {Promise<{ status: string, html: string }>} `status` is 'loaded' or 'failed'
     */
    FETCH_TENANT_RELEASE_BODY({ state, commit }, slug) {
      const cached = state.tenantReleaseBodies[slug];
      // Only a success is final. A previous failure is allowed to be retried by the caller.
      if (cached && cached.status === 'loaded') return Promise.resolve(cached);
      if (state.tenantBodyLoadingSlugs.includes(slug)) return Promise.resolve(cached || null);

      commit('ADD_TENANT_BODY_LOADING', slug);
      return tenantReleasesService
        .getBySlug(slug)
        .then((res) => {
          const body = { status: 'loaded', data: res.data || null };
          commit('SET_TENANT_RELEASE_BODY', { slug, body });
          return body;
        })
        .catch((err) => {
          warn('Failed to load release body for the changelog', slug, err);
          const body = { status: 'failed', data: null };
          commit('SET_TENANT_RELEASE_BODY', { slug, body });
          return body;
        })
        .finally(() => {
          commit('REMOVE_TENANT_BODY_LOADING', slug);
        });
    },

    FETCH_SUBSCRIPTION({ commit }) {
      return tenantReleasesService
        .getSubscription()
        .then((res) => {
          commit('SET_SUBSCRIPTION', res.data);
          return res.data;
        })
        .catch((err) => {
          $handleError(err);
        });
    },

    UPDATE_SUBSCRIPTION({ commit }, data) {
      return tenantReleasesService
        .updateSubscription(data)
        .then((res) => {
          commit('SET_SUBSCRIPTION', res.data);
          return res.data;
        })
        .catch((err) => {
          $handleError(err);
          throw err;
        });
    },
  },
};
